import {
  formatPhone,
  type PhoneSignInChannel,
  type SignInMethodKind,
  type SignInStartResponse,
  type User,
} from "@teamna/shared";
import type { CountryCode } from "libphonenumber-js";
import { useEffect, useState } from "react";
import { isApiError } from "@teamna/web-client/api/client.ts";
import { fetchSignInOptions, signOut } from "@teamna/web-client/auth/auth-store.ts";
import {
  useRemoveSignInMethod,
  useStartSignInMethod,
  useVerifySignInMethod,
} from "@teamna/web-client/hooks/use-member.ts";
import { Badge } from "./Badge.tsx";
import { Button } from "./Button.tsx";
import {
  channelWords,
  CODE_LENGTH,
  CodeBoxes,
  messageForError,
  otherPhoneChannel,
  switchChannelLabel,
  useResendTimer,
} from "./CodeFlow.tsx";
import { Field } from "./Field.tsx";
import { channelsFor, guessCountry, PhoneChannelButtons, PhoneNumberField, readPhone } from "./PhoneSignIn.tsx";
import { Notice, SectionHeader } from "./Section.tsx";
import { Sheet } from "./Sheet.tsx";

/**
 * Profile, "Sign-in methods" (auth spec V2, section 7.2). Two rows, mobile number and email. A
 * signed-in person adds the other one with a code, and can remove either one while the other is
 * verified. The person's own number and address show in full: masking is for lists of other people.
 * Nothing here merges accounts: a number or address that is on another account is
 * refused, and the only way to use it is to sign out and sign in with it.
 */

const LAST_METHOD_HINT = "Add another way to sign in first";

type RowState = "verified" | "unverified" | "missing";

function stateOf(value: string | null, verifiedAt: string | null): RowState {
  if (!value) return "missing";
  return verifiedAt ? "verified" : "unverified";
}

export function SignInMethodsSection({ user }: { user: User }) {
  const [adding, setAdding] = useState<{ kind: SignInMethodKind; prefill: string } | null>(null);
  const [removing, setRemoving] = useState<SignInMethodKind | null>(null);

  const phone = stateOf(user.phoneE164, user.phoneVerifiedAt);
  const email = stateOf(user.email, user.emailVerifiedAt);
  const verifiedCount = [phone, email].filter((state) => state === "verified").length;

  return (
    <section aria-labelledby="sign-in-methods">
      <SectionHeader title="Sign-in methods" />
      <div id="sign-in-methods" className="rounded-card border border-line bg-paper shadow-card">
        <MethodRow
          label="Mobile number"
          value={user.phoneE164 ? formatPhone(user.phoneE164) : null}
          state={phone}
          isLast={phone === "verified" && verifiedCount === 1}
          onAdd={() => setAdding({ kind: "phone", prefill: phone === "unverified" ? (user.phoneE164 ?? "") : "" })}
          onRemove={() => setRemoving("phone")}
        />
        <div className="mx-4 border-t border-line" />
        <MethodRow
          label="Email"
          value={user.email}
          state={email}
          isLast={email === "verified" && verifiedCount === 1}
          onAdd={() => setAdding({ kind: "email", prefill: email === "unverified" ? (user.email ?? "") : "" })}
          onRemove={() => setRemoving("email")}
        />
      </div>

      {adding ? <AddMethodSheet kind={adding.kind} prefill={adding.prefill} onClose={() => setAdding(null)} /> : null}
      {removing ? <RemoveMethodSheet kind={removing} onClose={() => setRemoving(null)} /> : null}
    </section>
  );
}

function MethodRow({
  label,
  value,
  state,
  isLast,
  onAdd,
  onRemove,
}: {
  label: string;
  value: string | null;
  state: RowState;
  isLast: boolean;
  onAdd: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5" data-testid={`method-${label}`}>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-ink-2">{label}</p>
        <p className="text-[15px] break-all text-ink">{value ?? "Not added"}</p>
        {state === "verified" && isLast ? <p className="text-[12px] text-ink-2">{LAST_METHOD_HINT}</p> : null}
      </div>
      {state === "verified" ? (
        <div className="flex shrink-0 items-center gap-2">
          <Badge tone="confirmed">Verified</Badge>
          <Button
            variant="secondary"
            aria-label={`Remove ${label.toLowerCase()}`}
            disabled={isLast}
            title={isLast ? LAST_METHOD_HINT : undefined}
            onClick={onRemove}
            className="min-h-10 px-3 text-[13px]"
          >
            Remove
          </Button>
        </div>
      ) : (
        <Button
          variant="secondary"
          aria-label={`${state === "unverified" ? "Verify" : "Add"} ${label.toLowerCase()}`}
          onClick={onAdd}
          className="min-h-10 shrink-0 px-3 text-[13px]"
        >
          {state === "unverified" ? "Verify" : "Add"}
        </Button>
      )}
    </div>
  );
}

/** Add or verify a mobile number or an email with a code. Also used by the owner sign-up. */
export function AddMethodSheet({
  kind,
  prefill,
  onClose,
}: {
  kind: SignInMethodKind;
  prefill: string;
  onClose: () => void;
}) {
  const start = useStartSignInMethod();
  const verify = useVerifySignInMethod();
  const [identifier, setIdentifier] = useState(prefill);
  const [channel, setChannel] = useState<PhoneSignInChannel>("whatsapp");
  const [country, setCountry] = useState<CountryCode>(() => readPhone(prefill, "AE")?.country ?? guessCountry(null));
  const [options, setOptions] = useState({ phoneChannels: ["whatsapp", "sms"] as PhoneSignInChannel[], smsCountries: ["AE"] });
  const [invalidNumber, setInvalidNumber] = useState(false);
  const [sent, setSent] = useState<SignInStartResponse | null>(null);
  const [code, setCode] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const noun = kind === "phone" ? "mobile number" : "email";
  const inUse = isApiError(error) && error.code === "IDENTIFIER_IN_USE";

  // Which buttons to show: the channels that really carry a code, and where SMS may go.
  useEffect(() => {
    if (kind !== "phone") return;
    let cancelled = false;
    fetchSignInOptions()
      .then((answer) => {
        if (!cancelled) setOptions({ phoneChannels: answer.phoneChannels, smsCountries: answer.smsCountries });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [kind]);

  function send(asked: PhoneSignInChannel = channel): void {
    setError(null);
    setInvalidNumber(false);
    let target = identifier.trim();
    if (kind === "phone") {
      const read = readPhone(identifier, country);
      if (!read) {
        setInvalidNumber(true);
        return;
      }
      target = read.e164;
    }
    // "Send by SMS instead" on the code step asks for the other channel, and later resends keep it.
    if (asked !== channel) setChannel(asked);
    start.mutate(
      { identifier: target, ...(kind === "phone" ? { channel: asked } : {}) },
      {
        onSuccess: (result) => {
          if ("alreadyVerified" in result && result.alreadyVerified) {
            setDone(true);
            return;
          }
          setSent(result as SignInStartResponse);
          setCode("");
        },
        onError: setError,
      },
    );
  }

  function confirm(value: string): void {
    if (!sent || value.length !== CODE_LENGTH || verify.isPending) return;
    setError(null);
    verify.mutate(
      { identifier: sent.identifier, code: value },
      { onSuccess: () => setDone(true), onError: setError },
    );
  }

  const title = done ? "Added" : kind === "phone" ? "Add a mobile number" : "Add an email";

  return (
    <Sheet open onClose={onClose} title={title} subtitle={done ? undefined : `You can then sign in with this ${noun}.`}>
      <div className="flex flex-col gap-4 pb-2">
        {done ? (
          <>
            <Notice tone="success" title={`Your ${noun} is verified`}>
              You can now sign in with it.
            </Notice>
            <Button size="lg" fullWidth onClick={onClose}>
              Done
            </Button>
          </>
        ) : sent ? (
          <CodeEntry
            sent={sent}
            code={code}
            onCode={(value) => {
              setCode(value);
              setError(null);
            }}
            onComplete={confirm}
            onResend={send}
            error={error}
            verifying={verify.isPending}
          />
        ) : (
          <>
            {kind === "phone" ? (
              <PhoneNumberField
                country={country}
                onCountryChange={(next) => {
                  setCountry(next);
                  setError(null);
                  setInvalidNumber(false);
                }}
                value={identifier}
                onChange={(value) => {
                  setIdentifier(value);
                  setError(null);
                  setInvalidNumber(false);
                }}
                error={invalidNumber ? "Enter a valid mobile number." : error && !inUse ? messageForError(error) : undefined}
              />
            ) : (
              <Field
                label="Email"
                value={identifier}
                onChange={(event) => {
                  setIdentifier(event.target.value);
                  setError(null);
                }}
                placeholder="you@example.com"
                inputMode="email"
                type="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                error={error && !inUse ? messageForError(error) : undefined}
              />
            )}
            {inUse ? (
              <div role="alert" className="flex flex-col gap-3 rounded-xl border border-cancelled/20 bg-cancelled-surface p-3.5">
                <p className="text-[14px] font-medium text-cancelled">{messageForError(error)}</p>
                <Button variant="danger" onClick={signOut}>
                  Sign out
                </Button>
              </div>
            ) : null}
            {kind === "phone" ? (
              channelsFor(country, options).length > 0 ? (
                <PhoneChannelButtons
                  channels={channelsFor(country, options)}
                  loading={start.isPending ? channel : null}
                  disabled={identifier.trim().length < 4}
                  onSend={(asked) => send(asked)}
                />
              ) : (
                <Notice tone="warning" title="Not available for this country">
                  We cannot send a code to this country yet.
                </Notice>
              )
            ) : (
              <Button
                size="lg"
                fullWidth
                loading={start.isPending}
                disabled={identifier.trim().length < 3}
                onClick={() => send()}
              >
                Send code
              </Button>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}

function CodeEntry({
  sent,
  code,
  onCode,
  onComplete,
  onResend,
  error,
  verifying,
}: {
  sent: SignInStartResponse;
  code: string;
  onCode: (value: string) => void;
  onComplete: (value: string) => void;
  onResend: (channel?: PhoneSignInChannel) => void;
  error: unknown;
  verifying: boolean;
}) {
  const resend = useResendTimer();
  const other = otherPhoneChannel(sent.channel);
  return (
    <>
      <p className="text-[15px] text-ink-2">
        We sent a code to <span className="font-semibold text-ink">{sent.identifier}</span> {channelWords(sent.channel)}.
      </p>
      {sent.simulated ? (
        <Notice tone="warning" title="Local sign-in">
          No message was sent. The code is 123456.
        </Notice>
      ) : null}
      <CodeBoxes value={code} onChange={onCode} onComplete={onComplete} invalid={Boolean(error)} channel={sent.channel} />
      {error ? (
        <p role="alert" className="text-[13px] font-medium text-cancelled">
          {messageForError(error)}
        </p>
      ) : null}
      <Button size="lg" fullWidth loading={verifying} disabled={code.length !== CODE_LENGTH} onClick={() => onComplete(code)}>
        Confirm
      </Button>
      <Button
        variant="ghost"
        disabled={resend.secondsLeft > 0}
        onClick={() => {
          onResend();
          resend.restart();
        }}
      >
        {resend.secondsLeft > 0 ? `Resend in ${resend.secondsLeft} s` : "Resend"}
      </Button>
      {other ? (
        <Button
          variant="ghost"
          disabled={resend.secondsLeft > 0}
          onClick={() => {
            onResend(other);
            resend.restart();
          }}
        >
          {switchChannelLabel(other)}
        </Button>
      ) : null}
    </>
  );
}

function RemoveMethodSheet({ kind, onClose }: { kind: SignInMethodKind; onClose: () => void }) {
  const remove = useRemoveSignInMethod();
  const noun = kind === "phone" ? "mobile number" : "email";
  return (
    <Sheet
      open
      onClose={onClose}
      title={`Remove your ${noun}?`}
      subtitle={`You can no longer sign in with this ${noun}.`}
      actions={
        <>
          {remove.error ? (
            <p role="alert" className="text-[13px] font-medium text-cancelled">
              {messageForError(remove.error)}
            </p>
          ) : null}
          <Button
            variant="danger"
            size="lg"
            fullWidth
            loading={remove.isPending}
            onClick={() => remove.mutate(kind, { onSuccess: onClose })}
          >
            Yes, remove it
          </Button>
          <Button variant="secondary" fullWidth onClick={onClose}>
            Keep it
          </Button>
        </>
      }
    />
  );
}
